import React, { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Box, FormControl, FormErrorMessage, FormLabel, Input, InputGroup } from '@chakra-ui/react';
import 'react-calendar/dist/Calendar.css';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { LooseValue } from 'react-date-picker/dist/cjs/shared/types';

import { DateRange } from './date-range-picker/types';
import { Dropdown } from './dropdown';

type DateFieldProps = {
  id: string;
  label: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  value?: DateRange | undefined;
  onChange?: (value?: DateRange) => void;
};

export const DateRangeField: FC<DateFieldProps> = (props) => {
  const { 
    id,
    label,
    value, 
    onChange, 
    required,
    disabled,
    error,
    helperText,
  } = props;
  const [dateValue, onChangeValue] = useState<LooseValue>(value ? [moment(value.startDate).toDate(), moment(value.endDate).toDate()] : undefined);
  
  useEffect(() => {
    if (!dateValue) {
      onChangeValue(undefined);
      onChange(undefined);
      return;
    }
    onChange({
      startDate: moment((dateValue as Date[])?.[0]),
      endDate: moment((dateValue as Date[])?.[1]),
    });
  }, [dateValue]);
  
  const portalContainerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  return (<InputGroup zIndex={1}>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
    >
      <Box
        w='100%'
        minH='var(--input-height)'
        display='flex'
        zIndex={1}
        __css={{
          '& .react-daterange-picker': { display: 'flex', width: '100%', alignItems: 'center'},
          '& .react-daterange-picker__wrapper': { 
            display: 'flex', width: '100%', alignItems: 'center', gap: 0,
            border: '1px solid',
            borderRadius: 'var(--input-border-radius)',
            paddingInlineStart: 4, paddingInlineEnd: 2, paddingY: 2,
          },
          '& .react-daterange-picker__inputGroup:not(:first-child)': {
            flex: 1
          },
          '& .react-daterange-picker__button': { width: '24px', height: '24px', },
          '& .react-daterange-picker__button:hover':   { backgroundColor: 'var(--chakra-colors-gray-100)', borderRadius: '50%'},
          '& .react-daterange-picker__button svg': { width: '12px', marginX: 'auto' },
          '& .react-daterange-picker__calendar': { display: 'none'},
        }}
        ref={fieldRef}
      >
        <DateRangePicker 
          onChange={onChangeValue} 
          value={dateValue} 
          format='dd.MM.yyyy' 
          isOpen={open}
          portalContainer={portalContainerRef.current} 
          onFocus={() => {
            setOpen(true);
          }}
          onClick={() =>  {
            setOpen(true);
          }}
        />
      </Box>
      <Input 
        position="absolute"
        opacity={0}
        placeholder={label}
        value={dateValue?.toString() || ''}
        contentEditable={false}
        isReadOnly
        height={0}
      />
      <FormLabel textColor="dark.300" width={value === undefined ? 'calc(100% - 70px)' : 'auto'} _groupFocusWithin={{ width: 'auto' }}>{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>

    <Dropdown 
      fieldRef={fieldRef}
      open={open}
      setOpen={setOpen}
      autoWidth
    >
      <Box 
        ref={portalContainerRef}
        __css={{
          '& .react-daterange-picker__calendar': { display: 'none'},
          '& .react-daterange-picker__calendar--closed': { display: 'none'},
          '& .react-daterange-picker__calendar--open': { display: 'block' },
          '& .react-calendar': { backgroundColor: 'white', borderRadius: 'md', overflow: 'hidden', borderColor: 'gray.200 !important' },
          '& .react-calendar__tile--now': { backgroundColor: 'light.500' },
          '& .react-calendar__tile--now:hover': { backgroundColor: 'light.600' },
        }}
      />

    </Dropdown>
  </InputGroup>);
};

export default DateRangeField;
